import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Camera, Trash, EnvelopeSimple, Plus, X, Image, CloudArrowUp } from '@phosphor-icons/react';
import { LoadingSpinner } from '../ui/loading-spinner';
import { getApiBaseUrl } from '../../utils/api';
import { toast } from 'sonner';
import imageCompression from 'browser-image-compression';

export function SessionPhotosDialog({
  open,
  onOpenChange,
  session
}) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [notifying, setNotifying] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open && session) {
      loadPhotos();
    }
  }, [open, session]);

  const loadPhotos = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/cloudinary/photos/${session.session_id}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setPhotos(data.photos || []);
      }
    } catch (error) {
      console.error('Load photos error:', error);
    } finally {
      setLoading(false);
    }
  };

  const uploadToCloudinary = async (file, signature) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('api_key', signature.api_key);
    formData.append('timestamp', signature.timestamp);
    formData.append('signature', signature.signature);
    formData.append('folder', signature.folder);

    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${signature.cloud_name}/image/upload`,
      { method: 'POST', body: formData }
    );

    const data = await response.json();
    
    if (!response.ok) {
      // Get actual error message from Cloudinary
      const errorMsg = data.error?.message || JSON.stringify(data);
      throw new Error(errorMsg);
    }

    return data;
  };

  // Compress image using browser-image-compression library
  const compressImage = async (file) => {
    const options = {
      maxSizeMB: 5,              // Max file size 5MB
      maxWidthOrHeight: 1920,    // Max dimension
      useWebWorker: true,        // Use web worker for faster processing
      fileType: 'image/jpeg',
      initialQuality: 0.8,
    };

    try {
      const compressedFile = await imageCompression(file, options);
      console.log(`Compressed: ${(file.size/1024/1024).toFixed(1)}MB -> ${(compressedFile.size/1024/1024).toFixed(1)}MB`);
      return compressedFile;
    } catch (error) {
      console.error('Compression error:', error);
      throw error;
    }
  };

  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);
    let uploadedCount = 0;
    const totalFiles = files.length;

    for (let i = 0; i < files.length; i++) {
      let file = files[i];
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image`);
        continue;
      }

      try {
        // Always compress to ensure reasonable file size
        toast.info(`Processing ${file.name}...`);
        try {
          file = await compressImage(file);
        } catch (compressError) {
          toast.error(`Compression failed for ${file.name}: ${compressError.message}`);
          continue;
        }

        // Step 1: Get signed upload params from backend
        const sigResponse = await fetch(
          `${getApiBaseUrl()}/api/cloudinary/signature?folder=sessions/${session.session_id}`,
          { credentials: 'include' }
        );

        if (!sigResponse.ok) {
          const errorText = await sigResponse.text();
          toast.error(`Signature failed: ${sigResponse.status} - ${errorText.substring(0, 100)}`);
          continue;
        }

        const signature = await sigResponse.json();

        // Step 2: Upload directly to Cloudinary
        let cloudinaryResult;
        try {
          cloudinaryResult = await uploadToCloudinary(file, signature);
        } catch (cloudError) {
          toast.error(`Cloudinary upload failed: ${cloudError.message}`);
          continue;
        }

        // Step 3: Save photo record to our database
        const saveResponse = await fetch(
          `${getApiBaseUrl()}/api/cloudinary/photos`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              session_id: session.session_id,
              public_id: cloudinaryResult.public_id,
              url: cloudinaryResult.url,
              secure_url: cloudinaryResult.secure_url,
              width: cloudinaryResult.width,
              height: cloudinaryResult.height,
              format: cloudinaryResult.format
            })
          }
        );

        if (saveResponse.ok) {
          uploadedCount++;
          toast.success(`Uploaded ${file.name}`);
        } else {
          const saveError = await saveResponse.text();
          toast.error(`Save failed: ${saveError.substring(0, 100)}`);
        }

        setUploadProgress(Math.round(((i + 1) / totalFiles) * 100));

      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Error: ${error.message || 'Unknown error'}`);
      }
    }

    if (uploadedCount > 0) {
      toast.success(`Uploaded ${uploadedCount} photo(s) to cloud storage`);
      loadPhotos();
    }

    setUploading(false);
    setUploadProgress(0);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (photo) => {
    if (!window.confirm('Delete this photo?')) return;

    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/cloudinary/photos/${encodeURIComponent(photo.public_id)}`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );

      if (response.ok) {
        toast.success('Photo deleted');
        setPhotos(photos.filter(p => p.public_id !== photo.public_id));
      } else {
        toast.error('Failed to delete photo');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete photo');
    }
  };

  const handleNotifyParents = async () => {
    if (photos.length === 0) {
      toast.error('No photos to share');
      return;
    }

    setNotifying(true);
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/admin/sessions/${session.session_id}/photos/notify`,
        {
          method: 'POST',
          credentials: 'include'
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send notifications');
      }
    } catch (error) {
      console.error('Notify error:', error);
      toast.error('Failed to send notifications');
    } finally {
      setNotifying(false);
    }
  };

  // Generate optimized thumbnail URL from Cloudinary
  const getThumbnailUrl = (photo) => {
    // Use Cloudinary transformations for optimized thumbnails
    if (photo.secure_url) {
      return photo.secure_url.replace('/upload/', '/upload/c_fill,w_400,h_400,q_auto,f_auto/');
    }
    return photo.url;
  };

  // Get full-size optimized URL
  const getFullUrl = (photo) => {
    if (photo.secure_url) {
      return photo.secure_url.replace('/upload/', '/upload/q_auto,f_auto/');
    }
    return photo.url;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Camera size={24} weight="duotone" className="text-primary" />
              SESSION PHOTOS
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Session Info */}
            <div className="text-sm text-muted-foreground">
              {session?.title && <span className="font-medium text-primary">{session.title} • </span>}
              {session?.date} • {session?.start_time}
            </div>

            {/* Upload Section */}
            <div className="flex gap-2 flex-wrap">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleFileSelect}
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="btn-primary"
                data-testid="add-photos-btn"
              >
                {uploading ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Uploading {uploadProgress}%
                  </>
                ) : (
                  <>
                    <CloudArrowUp size={16} className="mr-2" />
                    Add Photos
                  </>
                )}
              </Button>

              {photos.length > 0 && (
                <Button
                  variant="outline"
                  onClick={handleNotifyParents}
                  disabled={notifying}
                  data-testid="notify-parents-btn"
                >
                  {notifying ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <EnvelopeSimple size={16} className="mr-2" />
                      Notify Parents
                    </>
                  )}
                </Button>
              )}
            </div>

            {/* Upload Progress Bar */}
            {uploading && uploadProgress > 0 && (
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            )}

            {/* Photos Grid */}
            {loading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : photos.length === 0 ? (
              <div className="text-center py-12 bg-muted/20 rounded-lg">
                <Image size={48} className="mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">No photos yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Click "Add Photos" to upload session photos to cloud storage
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {photos.map((photo) => (
                  <div
                    key={photo.public_id}
                    className="relative group aspect-square rounded-lg overflow-hidden bg-muted cursor-pointer"
                    onClick={() => setSelectedPhoto(photo)}
                    data-testid={`photo-${photo.public_id}`}
                  >
                    <img
                      src={getThumbnailUrl(photo)}
                      alt="Session photo"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(photo);
                        }}
                        className="p-2 bg-red-500 rounded-full text-white hover:bg-red-600 transition-colors"
                        data-testid={`delete-photo-${photo.public_id}`}
                      >
                        <Trash size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              {photos.length} photo(s) • Stored in Cloudinary CDN • Max 10MB per photo
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Full-size Photo Viewer */}
      <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
        <DialogContent className="sm:max-w-4xl p-2">
          <button
            onClick={() => setSelectedPhoto(null)}
            className="absolute top-2 right-2 p-2 bg-black/50 rounded-full text-white hover:bg-black/70 z-10"
          >
            <X size={20} />
          </button>
          {selectedPhoto && (
            <img
              src={getFullUrl(selectedPhoto)}
              alt="Session photo"
              className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
